import { apiRequest } from '@/utils/api-request';
import type {
  ArtifactAccessListResponse,
  ArtifactShareResponse,
  ShareArtifactRequest,
} from '@/types/artifacts';
import type {
  RevokeResponse,
  ShareInviteResponse,
  ShareListResponse,
} from '@/types/share-types';

export async function shareArtifact(
  orgId: string,
  artifactId: string,
  data: ShareArtifactRequest,
): Promise<ShareInviteResponse | { detail: string }> {
  const response = await apiRequest<ArtifactShareResponse>({
    url: `/api/v1/orgs/${orgId}/artifacts/${artifactId}/shares`,
    method: 'POST',
    target: 'workflow',
    body: data as unknown as Record<string, unknown>,
  });

  if (response.detail) {
    return { detail: response.detail };
  }

  if (!response.data) {
    throw new Error('Failed to share artifact');
  }

  const grantedEmails = new Set(
    response.data.granted.map((a) => a.email ?? a.user_id),
  );
  const skipped = new Set(response.data.skipped);
  return {
    shared_with: data.emails.map((email) => {
      if (skipped.has(email)) {
        return {
          email,
          status: 'failed' as const,
          reason: 'User not found or could not be granted access',
        };
      }
      if (grantedEmails.has(email)) {
        return { email, status: 'granted' as const };
      }
      return { email, status: 'already_has_access' as const };
    }),
    link: '',
  };
}

export async function listArtifactShares(
  orgId: string,
  artifactId: string,
): Promise<ShareListResponse> {
  const response = await apiRequest<ArtifactAccessListResponse>({
    url: `/api/v1/orgs/${orgId}/artifacts/${artifactId}/shares`,
    method: 'GET',
    target: 'workflow',
  });

  if (!response.data) {
    throw new Error(response.detail ?? 'Failed to list shares');
  }

  return {
    access: response.data.access.map((item) => ({
      email: item.email ?? item.user_id,
      role: item.role_id as ShareListResponse['access'][number]['role'],
    })),
  };
}

export async function revokeArtifactShare(
  orgId: string,
  artifactId: string,
  email: string,
): Promise<RevokeResponse | { detail: string }> {
  const response = await apiRequest<RevokeResponse>({
    url: `/api/v1/orgs/${orgId}/artifacts/${artifactId}/shares/revoke`,
    method: 'POST',
    target: 'workflow',
    body: { email } as unknown as Record<string, unknown>,
  });

  if (response.detail && !('status' in (response.data ?? {}))) {
    return { detail: response.detail };
  }

  return response.data as unknown as RevokeResponse;
}
